import { zodResolver } from "@hookform/resolvers/zod";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AlertTriangle, Check, Plus, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { apiErrorCode, apiErrorMessage } from "@/api/client";
import DateInput from "@/components/ui/DateInput";
import TimeInput from "@/components/ui/TimeInput";
import { useGridTime } from "@/hooks/useGridTime";
import {
  clientReservations as clientReservationsApi,
  rewards as rewardsApi,
  clients as clientsApi,
  fees as feesApi,
  orders as ordersApi,
} from "@/api/endpoints";
import {
  Alert,
  Checkbox,
  Input,
  Modal,
  Select,
  Spinner,
} from "@/components/ui";
import { type ClientLike } from "@/features/clients/ClientCombobox";
import LessonRewardModal from "@/features/rewards/LessonRewardModal";
import PaymentDetailsModal from "@/features/pricing/PaymentDetailsModal";
import PaymentQuoteModal from "@/features/pricing/PaymentQuoteModal";
import RewardQuoteModal from "@/features/rewards/RewardQuoteModal";
import {
  addDays,
  cn,
  formatMoney,
  formatTime,
  toISODate,
  toISODateTime,
} from "@/lib/utils";
import { useBootstrap } from "@/hooks/useBootstrap";
import { useAuth } from "@/store/auth";
import type {
  CalendarGrid,
  CalendarResponse,
  Client,
  ClientReservation,
} from "@/types/api";
import { PaymentType, ReserveStatus } from "@/types/api";
import { CenterSelect, useCenterFilter, useCenters } from "@/features/centers/useCenters";

import {
  useDeleteClientReservation,
  useSaveClientReservation,
  useSaveClientReservations,
} from "./useCalendar";

const schema = z.object({
  client_id: z.number().nullable(),
  fname: z.string().optional(),
  sname: z.string().min(1),
  email: z.string().email().or(z.literal("")).optional(),
  tel: z.string().optional(),
  client_note: z.string().optional(),
  is_children: z.boolean(),
  // Úroveň, nie príznak „začiatočník“ - ten sa z nej odvodí na serveri.
  levelid: z.number().nullable(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
  status_id: z.number(),
  lang_id: z.number(),
  course_id: z.number(),
  numbers: z.number().min(1),
  note: z.string().max(100).optional(),
  is_school_course: z.boolean(),
  // Len pri „len označení úhrady“ (`settings.simple_payments`) - inak sa
  // úhrada zapisuje v detailoch platby.
  client_paid: z.number().optional(),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  reservation: ClientReservation | null;
  day: string;
  part: number;
  blockId: number | null;
  grid: CalendarGrid;
  codebooks: CalendarResponse;
  onClose: () => void;
}

export default function ClientReservationModal({
  reservation,
  day,
  part,
  blockId,
  grid,
  codebooks,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const { data: boot } = useBootstrap();
  // Časy po dielikoch kalendára (rozdelenie hodín v nastaveniach).
  const gridTime = useGridTime();
  /** Škola eviduje platby inde - tu len označí, či a ako je zaplatené. */
  const simplePayments = Boolean(boot?.settings.simple_payments);
  const save = useSaveClientReservation();
  const saveMany = useSaveClientReservations();
  const remove = useDeleteClientReservation();
  const qc = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  /**
   * Ďalšie termíny tej istej rezervácie — kurz rozložený do viacerých dní.
   *
   * Prvý termín zostáva v poliach formulára; tu sú len tie ďalšie. Zoznam je
   * prázdny, kým operátor nepridá aspoň jeden, takže bežná jednorazová lekcia
   * vyzerá presne ako doteraz.
   */
  const [extra, setExtra] = useState<
    Array<{ date: string; start: string; end: string }>
  >([]);
  const [savedId, setSavedId] = useState<number | null>(
    reservation?.id ?? null,
  );
  //: Variabilný symbol práve uloženej lekcie. Drží sa zvlášť, lebo čerstvo
  //: založená rezervácia ešte nie je v `reservation` - a bez symbolu by okno
  //: platby nemalo objednávku, ktorú má ukázať.
  const [savedRef, setSavedRef] = useState<number | null>(
    reservation?.payment_ref ?? null,
  );
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  /** Vlastné bonusy inštruktora (Nastavenia → Odmeny).
   *  Zaškrtnutý, no medzitým vypnutý bonus ostáva v zozname a odíde späť. */
  const [bonusIds, setBonusIds] = useState<number[]>(reservation?.bonus_ids ?? []);
  const [boundClient, setBoundClient] = useState<ClientLike | null>(
    reservation?.client ?? null,
  );

  /** Whose reward the lesson feeds - shown in the reward modal's title. */
  const currentBlock = codebooks.instructor_reservations.find(
    (b) => b.id === (reservation?.instructor_reservation_id ?? blockId),
  );
  const instructorName = currentBlock?.instructor?.full_name ?? null;

  /** Stredisko (`sql/040`): lekcia v bloku je v stredisku bloku; bez
   *  inštruktora sa vyberá (predvolene to, na ktoré je kalendár zúžený). */
  const { multi: multiCenter, nameOf: centerName, centers: allCenters } = useCenters();
  const [filteredCenters] = useCenterFilter();
  const [ownCenterId, setOwnCenterId] = useState<number>(
    reservation?.center_id ?? (filteredCenters.length === 1 ? filteredCenters[0] : 1),
  );
  const centerId = blockId ? (currentBlock?.center_id ?? ownCenterId) : ownCenterId;

  /** Účet, na ktorý má klient platiť (`sql/042`) - ponúka sa, keď má
   *  stredisko viac účtov. `null` = predvolený účet strediska. Stredisko
   *  bez vlastných účtov platí na účty hlavného (`centers.account_center`). */
  const ownAccounts = allCenters.find((c) => c.id === centerId)?.accounts ?? [];
  const centerAccounts = ownAccounts.length
    ? ownAccounts
    : (allCenters.find((c) => c.id === 1)?.accounts ?? []);
  const [accountId, setAccountId] = useState<number | null>(reservation?.bank_account_id ?? null);
  useEffect(() => {
    // Iné stredisko - účet z pôvodného tu neplatí.
    if (accountId && centerAccounts.length && !centerAccounts.some((a) => a.id === accountId)) {
      setAccountId(null);
    }
  }, [accountId, centerAccounts]);
  const defaultAccount = centerAccounts.find((a) => a.is_default);
  const accountValue = accountId && accountId !== defaultAccount?.id ? String(accountId) : "";
  const accountLabel = (a: { name: string; iban: string }) => {
    const iban = a.iban.replace(/(.{4})/g, "$1 ").trim();
    return a.name ? `${a.name} · ${iban}` : iban;
  };
  const accountPicker =
    centerAccounts.length > 1 ? (
      <label className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">{t("accounts.payTo")}</span>
        <Select
          className="w-auto"
          value={accountValue}
          disabled={!user?.can_write}
          onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}
        >
          {centerAccounts.map((a) => (
            <option key={a.id} value={a.is_default ? "" : String(a.id)}>
              {accountLabel(a)}
              {a.is_default ? ` (${t("accounts.defaultShort")})` : ""}
            </option>
          ))}
        </Select>
      </label>
    ) : null;

  /** Predvolená úroveň - začiatočník (`BEGIN`). Prázdnu ani „0“ zo starých
   *  dát formulár neponúka. */
  const beginnerId =
    codebooks.levels?.find((level) => level.key === "BEGIN")?.id ??
    codebooks.levels?.[0]?.id ??
    null;

  const slotStart = grid.slots[part - 1]?.label ?? "09:00";
  const slotEnd = addHours(slotStart, 1, grid.end_hour);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: reservation
      ? {
          client_id: reservation.client_id,
          fname: reservation.client?.fname ?? "",
          sname: reservation.client?.sname ?? "",
          email: reservation.client?.email ?? "",
          tel: reservation.client?.tel ?? "",
          client_note: reservation.client?.note ?? "",
          is_children: reservation.client?.is_children ?? false,
          levelid: reservation.client?.levelid || beginnerId,
          date: reservation.start?.slice(0, 10) ?? day,
          start: formatTime(reservation.start),
          end: formatTime(reservation.end),
          status_id: reservation.status_id ?? 2,
          lang_id: reservation.lang_id,
          course_id: reservation.course_id,
          numbers: reservation.numbers,
          note: reservation.note ?? "",
          is_school_course: reservation.is_school_course ?? false,
          client_paid: reservation.client_paid ?? PaymentType.Unpaid,
        }
      : {
          client_id: null,
          fname: "",
          sname: "",
          email: "",
          tel: "",
          client_note: "",
          is_children: false,
          levelid: beginnerId,
          date: day,
          start: slotStart,
          end: slotEnd,
          status_id: 2,
          lang_id: 1,
          course_id: 1,
          numbers: 1,
          note: "",
          is_school_course: false,
        },
  });

  const sname = watch("sname");
  const tel = watch("tel");
  const email = watch("email");

  /**
   * Late duplicate guard: the operator skipped the picker and typed a phone or
   * e-mail that already belongs to somebody. Offer to bind that client instead
   * of creating a second record.
   */
  const duplicateProbe = (tel || "").replace(/\s/g, "");
  const { data: duplicates } = useQuery({
    queryKey: ["client-duplicate-probe", duplicateProbe, email],
    queryFn: () =>
      clientsApi.list(
        duplicateProbe.length >= 6
          ? { phone: duplicateProbe, limit: 3 }
          : { q: email, limit: 3 },
      ),
    enabled:
      !boundClient &&
      (duplicateProbe.length >= 6 || (email ?? "").includes("@")),
  });
  const duplicate = duplicates?.results?.[0] ?? null;

  /**
   * Živé vyhľadávanie zákazníka podľa poľa, ktoré operátor práve píše -
   * priezvisko, meno, telefón či e-mail. Výsledky sú v pravom stĺpci.
   */
  const fname = watch("fname");
  const [searchTerm, setSearchTerm] = useState({ field: "", value: "" });
  // Výsledky sú v plávajúcom zozname, aby desať nájdených nezväčšilo okno.
  const [listOpen, setListOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!listOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!listRef.current?.contains(e.target as Node)) setListOpen(false);
    };
    // Esc zavrie len zoznam, nie celé okno: zachytí sa na `window` skôr,
    // než k nemu dôjde poslucháč `Modal` na `document`.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setListOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [listOpen]);
  const lastTyped = useRef({ sname: sname ?? "", fname: fname ?? "", tel: tel ?? "", email: email ?? "" });
  useEffect(() => {
    const now = { sname: sname ?? "", fname: fname ?? "", tel: tel ?? "", email: email ?? "" };
    const fields = Object.keys(now) as Array<keyof typeof now>;
    const changed = fields.find((field) => now[field] !== lastTyped.current[field]);
    lastTyped.current = now;
    if (!changed) return;
    const timer = setTimeout(() => {
      setSearchTerm({ field: changed, value: now[changed].trim() });
      setListOpen(true);
    }, 250);
    return () => clearTimeout(timer);
  }, [sname, fname, tel, email]);

  const phoneDigits =
    searchTerm.field === "tel" ? searchTerm.value.replace(/\D/g, "") : "";
  const searchParams =
    searchTerm.field === "tel" && phoneDigits.length >= 9
      ? // Celé číslo: podľa posledných deviatich číslic, bez ohľadu na
        // medzery a predvoľbu, ako ho má uložené.
        { phone: phoneDigits, limit: 8 }
      : { q: searchTerm.value, limit: 8 };
  const { data: found, isFetching: searching } = useQuery({
    queryKey: ["client-search", searchParams],
    queryFn: () => clientsApi.list(searchParams),
    enabled: !boundClient && searchTerm.value.length >= 2,
    placeholderData: keepPreviousData,
  });
  const results = searchTerm.value.length >= 2 ? (found?.results ?? []) : [];

  const { data: fees } = useQuery({
    queryKey: ["fees", savedId],
    queryFn: () => feesApi.list({ client_reservation_id: savedId, limit: 50 }),
    enabled: Boolean(savedId),
  });

  /** Kým rezervácia nie je uložená, cena je len orientačná - vypočíta ju
   *  server tým istým cenníkom, ale nič nezapíše. Položky a variabilný
   *  symbol vzniknú až uložením (`quote` v `reservations/views.py`). */
  const quoteBody = {
    terms: [
      { date: watch("date"), start: watch("start"), end: watch("end") },
      ...extra,
    ].map((term) => ({
      start: toISODateTime(term.date, term.start),
      end: toISODateTime(term.date, term.end),
    })),
    numbers: watch("numbers"),
    lang_id: watch("lang_id"),
    status_id: watch("status_id"),
    // Cenník strediska (`sql/040`).
    center_id: centerId,
  };
  const { data: quote } = useQuery({
    queryKey: ["reservation-quote", quoteBody],
    queryFn: () => clientReservationsApi.quote(quoteBody),
    enabled: !savedId && Boolean(user?.can_write),
    placeholderData: keepPreviousData,
  });

  /** Odmena pre inštruktora pred uložením - rovnako orientačne, tými istými
   *  kalkulačkami ako pri uložení (`quote_lessons` v `rewards/views.py`). */
  const { data: rewardQuote } = useQuery({
    queryKey: ["reward-quote", blockId, quoteBody, bonusIds],
    queryFn: () =>
      rewardsApi.quoteLessons({ ...quoteBody, instructor_reservation_id: blockId, bonus_ids: bonusIds }),
    enabled: !savedId && Boolean(user?.can_write),
    placeholderData: keepPreviousData,
  });

  /** Rovnaká otázka ako v „detaily platby“ - je objednávka už celá
   *  uhradená? Úhrada sa zapisuje len tam; tu sa ukazuje stav. */
  const { data: order } = useQuery({
    queryKey: ["order", savedRef],
    queryFn: () => ordersApi.get(savedRef as number),
    enabled: Boolean(savedRef),
  });
  const settled = order?.settled ?? false;
  /**
   * Lekcia na vydanom doklade sa editovať nedá - zmena termínu, klienta či
   * ceny by rozišla to, čo má zákazník v ruke, s tým, čo je v systéme. Doklad
   * sa najprv stornuje v „Detailoch platby“.
   *
   * Uhradenú objednávku upraviť možno bez trvalého upozornenia - pýta sa
   * až pri uložení, a len keď sa cena naozaj zmení (`paidChangeQuestion`).
   * Server zmenu bez `confirm_paid` odmietne. Zmazať ju
   * nejde ani tak - platba by visela na symbole bez lekcie.
   */
  const receiptLocked = Boolean(reservation?.has_issued_receipt);
  const paidEdit =
    !receiptLocked &&
    (simplePayments
      ? (reservation?.client_paid ?? PaymentType.Unpaid) !== PaymentType.Unpaid
      : settled);

  useEffect(() => {
    setSavedId(reservation?.id ?? null);
    setSavedRef(reservation?.payment_ref ?? null);
    setBoundClient(reservation?.client ?? null);
  }, [reservation?.id, reservation?.client, reservation?.payment_ref]);

  /** Bind a client from the picker and prefill everything we know about them. */
  function bindClient(client: Client) {
    setBoundClient(client);
    setValue("client_id", client.id);
    setValue("fname", client.fname ?? "");
    setValue("sname", client.sname ?? "");
    setValue("email", client.email ?? "");
    setValue("tel", client.tel ?? "");
    setValue("client_note", client.note ?? "");
    setValue("is_children", client.is_children);
    setValue("levelid", client.levelid || beginnerId);

    // The client's language/course are only *defaults* for a new booking.
    // An existing reservation keeps whatever was agreed for that lesson.
    if (!savedId) {
      if (client.lang_id) setValue("lang_id", client.lang_id);
      if (client.course_id) setValue("course_id", client.course_id);
    }
  }

  function unbindClient() {
    setBoundClient(null);
    setValue("client_id", null);
  }

  /** „Nový zákazník“ - odpojiť priradeného a začať s prázdnymi poľami. */
  function newClient() {
    unbindClient();
    for (const field of ["fname", "sname", "tel", "email", "client_note"] as const) {
      setValue(field, "");
    }
    setValue("is_children", false);
    setValue("levelid", beginnerId);
    saveClient.reset();
  }

  /** „Uložiť zákazníka“ - len karta zákazníka, bez rezervácie. Nový sa
   *  založí a hneď priradí oknu, existujúci sa upraví. */
  const saveClient = useMutation({
    mutationFn: async () => {
      const valid = await trigger(["sname", "email"]);
      if (!valid) return null;
      const v = getValues();
      const identity = {
        fname: v.fname ?? "",
        sname: v.sname,
        tel: v.tel || null,
        email: v.email || null,
        note: v.client_note || null,
        is_children: v.is_children,
        levelid: v.levelid,
      };
      return boundClient
        ? clientsApi.update(boundClient.id, identity)
        : clientsApi.create({ ...identity, lang_id: v.lang_id, course_id: v.course_id });
    },
    onSuccess: (client) => {
      if (!client) return;
      bindClient(client);
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  /** To, čo je pri jednom aj pri viacerých termínoch rovnaké. */
  function payloadFrom(values: FormValues) {
    return {
      client_id: values.client_id,
      instructor_reservation_id: blockId,
      fname: values.fname ?? "",
      sname: values.sname,
      email: values.email || null,
      tel: values.tel || null,
      client_note: values.client_note ?? "",
      is_children: values.is_children,
      levelid: values.levelid,
      start: toISODateTime(values.date, values.start),
      end: toISODateTime(values.date, values.end),
      status_id: values.status_id,
      lang_id: values.lang_id,
      course_id: values.course_id,
      numbers: values.numbers,
      note: values.note ?? "",
      ...(simplePayments ? { client_paid: values.client_paid ?? PaymentType.Unpaid } : {}),
      is_school_course: values.is_school_course,
      bonus_ids: bonusIds,
      center_id: centerId,
      // Len keď sa dá vybrať - inak účet objednávky ostáva, ako je.
      ...(centerAccounts.length > 1 ? { bank_account_id: accountValue ? Number(accountValue) : null } : {}),
    };
  }

  /** Otázka pred uložením uhradenej rezervácie, alebo `null`, keď sa cena
   *  nemení. Keď sa výpočet nepodarí, radšej sa opýta všeobecne. */
  async function paidChangeQuestion(id: number, values: FormValues) {
    if (values.status_id === ReserveStatus.Cancelled && reservation?.status_id !== ReserveStatus.Cancelled) {
      return t("reservation.cancelPaidConfirm");
    }
    try {
      const { delta } = await clientReservationsApi.pricePreview(id, {
        start: toISODateTime(values.date, values.start),
        end: toISODateTime(values.date, values.end),
        numbers: values.numbers,
        lang_id: values.lang_id,
        status_id: values.status_id,
      });
      const change = Number.parseFloat(delta);
      if (!change) return null;
      const amount = formatMoney(Math.abs(change));
      return change > 0
        ? t("reservation.paidPriceUp", { amount })
        : t("reservation.paidPriceDown", { amount });
    } catch {
      return t("reservation.paidPriceChange");
    }
  }

  /** Pridať ďalší termín — predvolene o deň neskôr, v ten istý čas.
   *
   *  Kurz býva v po sebe idúcich dňoch v rovnakú hodinu, takže sa najčastejšie
   *  netreba dotknúť ničoho; kto to má inak, prepíše dva vstupy.
   */
  function addTerm() {
    const last = extra[extra.length - 1];
    const base = last ?? {
      date: watch("date"),
      start: watch("start"),
      end: watch("end"),
    };
    setExtra([
      ...extra,
      {
        date: toISODate(addDays(new Date(base.date), 1)),
        start: base.start,
        end: base.end,
      },
    ]);
  }

  function setTerm(
    index: number,
    patch: Partial<{ date: string; start: string; end: string }>,
  ) {
    setExtra(
      extra.map((term, i) => (i === index ? { ...term, ...patch } : term)),
    );
  }

  /** Preložená správa pre chyby, ktoré majú vlastný kód - viď `apply_flag`. */
  function submitErrorMessage(err: unknown): string {
    if (apiErrorCode(err) === "payment.alreadySettled") {
      return t("reservation.paidLocked");
    }
    if (apiErrorCode(err) === "reservation.bonusPaid") {
      return t("reservation.bonusPaid");
    }
    if (apiErrorCode(err) === "reservation.mixedCenters") {
      return t("centers.mixedCenters");
    }
    if (apiErrorCode(err) === "reservation.invalidAccount") {
      return t("accounts.invalidForCenter");
    }
    return apiErrorMessage(err, t("errors.generic"));
  }

  async function submit(values: FormValues, close: boolean) {
    setError(null);

    // Viac termínov ide vlastnou cestou: server ich založí v jednej
    // transakcii a klienta upsertne raz. Slučka cez `save` by pri chybe
    // v strede nechala polovicu kurzu a operátor by nevedel, kde skončil.
    if (!savedId && extra.length) {
      try {
        await saveMany.mutateAsync({
          ...payloadFrom(values),
          occurrences: extra.map((term) => ({
            start: toISODateTime(term.date, term.start),
            end: toISODateTime(term.date, term.end),
          })),
        });
        onClose();
      } catch (err) {
        setError(submitErrorMessage(err));
      }
      return;
    }

    // Pýtať sa len vtedy, keď sa cena naozaj pohne - a povedať o koľko.
    // Poznámka, kontakt či presun pri rovnakej dĺžke na úhradu nevplývajú.
    // Server potvrdenie vyžaduje pri každej zmene uhradenej rezervácie,
    // preto sa posiela vždy.
    if (paidEdit && savedId && PRICE_FIELDS.some((field) => dirtyFields[field])) {
      const question = await paidChangeQuestion(savedId, values);
      if (question && !window.confirm(question)) return;
    }

    try {
      const result = await save.mutateAsync({
        id: savedId,
        ...payloadFrom(values),
        ...(paidEdit ? { confirm_paid: true } : {}),
      });
      if (close) {
        onClose();
      } else {
        setSavedId(result.id);
        setSavedRef(result.payment_ref);
        setValue("client_id", result.client_id);
        if (simplePayments) setValue("client_paid", result.client_paid);
        if (result.client) setBoundClient(result.client);
      }
    } catch (err) {
      setError(submitErrorMessage(err));
    }
  }

  const feeTotal = (fees?.results ?? []).reduce(
    (sum, f) => sum + Number.parseFloat(f.amount || "0"),
    0,
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        reservation
          ? t("calendar.editClientReservation")
          : t("calendar.newClientReservation")
      }
      footer={
        <>
          {/* Vymazať len rezerváciu bez peňazí - uhradenú (aj čiastočne) treba
              zrušiť a platbu vrátiť v detailoch platby (`reservation.cancelOnly`). */}
          {savedId && !receiptLocked && !(order && Number.parseFloat(order.paid) > 0) ? (
            <button
              type="button"
              className="btn-danger mr-auto"
              onClick={() => {
                if (window.confirm(t("reservation.deleteConfirm"))) {
                  remove.mutate(savedId, { onSuccess: onClose });
                }
              }}
            >
              <Trash2 size={15} />
              {t("global.delete")}
            </button>
          ) : null}
          <button type="button" className="btn-secondary" onClick={onClose}>
            {receiptLocked ? t("global.close") : t("global.cancel")}
          </button>
          {receiptLocked ? null : (
            <button
              type="button"
              className="btn-secondary"
              disabled={isSubmitting}
              onClick={handleSubmit((v) => submit(v, false))}
            >
              {t("global.save")}
            </button>
          )}
          {receiptLocked ? null : (
            <button
              type="button"
              className="btn-primary"
              disabled={isSubmitting}
              onClick={handleSubmit((v) => submit(v, true))}
            >
              {isSubmitting ? <Spinner /> : null}
              {t("global.saveAndClose")}
            </button>
          )}
        </>
      }
    >
      {error ? (
        <div className="mb-2">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : null}

      {receiptLocked ? (
        <div className="mb-2">
          <Alert variant="info">{t("reservation.editLockedReceipt")}</Alert>
        </div>
      ) : null}

      {/* Rozloženie podľa pôvodného formulára: popisky vľavo, polia husto
          vedľa seba a sekcie odlíšené pozadím - zákazník zelený, rezervácia
          biela, cena sivá. Oko tak hneď vie, v ktorej časti práve je. */}
      <form
        className="rounded border border-slate-200"
        onSubmit={handleSubmit((v) => submit(v, true))}
      >
        <fieldset
          className={cn(
            "rounded-t p-2.5 transition-colors",
            // Zelená = zákazník je priradený, oranžová = nový, ešte neuložený.
            boundClient ? "bg-[#e3efd9]" : "bg-[#fdefd9]",
          )}
          disabled={receiptLocked}
        >
          <div className="grid gap-x-4 gap-y-2 lg:grid-cols-[minmax(0,1fr)_15rem]">
            <div className="space-y-1.5">
              <Row
                label={t("reservation.form.surnameName")}
                error={errors.sname && t("errors.required")}
              >
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder={t("reservation.surname")}
                    autoFocus={!reservation}
                    {...register("sname")}
                  />
                  <Input placeholder={t("reservation.firstname")} {...register("fname")} />
                </div>
              </Row>
              <Row
                label={t("reservation.form.phoneEmail")}
                error={errors.email && t("errors.invalidEmail")}
              >
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t("reservation.phone")} {...register("tel")} />
                  <Input
                    type="email"
                    placeholder={t("reservation.email")}
                    {...register("email")}
                  />
                </div>
              </Row>
              <Row label={t("reservation.form.clientNote")}>
                <Input {...register("client_note")} />
              </Row>
              <div className="flex flex-wrap items-center gap-2 sm:pl-[9rem]">
                <button
                  type="button"
                  className="btn-primary py-1 text-xs uppercase"
                  disabled={saveClient.isPending}
                  onClick={() => saveClient.mutate()}
                >
                  {saveClient.isPending ? <Spinner className="h-3 w-3" /> : null}
                  {t("reservation.form.saveClient")}
                </button>
                <button
                  type="button"
                  className="btn-secondary py-1 text-xs uppercase"
                  onClick={newClient}
                >
                  {t("reservation.form.newClient")}
                </button>
                {saveClient.isSuccess && saveClient.data ? (
                  <span className="text-xs text-green-700">
                    {t("reservation.form.clientSaved")}
                  </span>
                ) : null}
                {saveClient.isError ? (
                  <span className="text-xs text-red-600">
                    {apiErrorMessage(saveClient.error, t("errors.generic"))}
                  </span>
                ) : null}
              </div>
              {boundClient ? (
                <p className="text-xs text-slate-600 sm:pl-[9rem]">
                  {t("clients.editHint")}
                </p>
              ) : null}
              {/* Pod poľami, nie vpravo - tam ho prekrýval rozbalený zoznam. */}
              {duplicate && !boundClient ? (
                <div className="sm:pl-[9rem]">
                  <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs">
                    <AlertTriangle size={15} className="shrink-0 text-amber-600" />
                    <div className="min-w-0 flex-1">
                      <div className="text-amber-900">{t("clients.possibleDuplicate")}</div>
                      <div className="truncate text-amber-800">
                        {duplicate.full_name} ·{" "}
                        {[duplicate.tel, duplicate.email].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary shrink-0 py-0.5 text-xs"
                      onClick={() => bindClient(duplicate)}
                    >
                      {t("clients.useExisting")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Zoznam zákazníkov - plní sa sám, keď operátor píše do
                ktoréhokoľvek poľa vľavo. Kliknutím sa zákazník priradí. */}
            <div>
              <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-slate-700">
                {t("reservation.form.clientList")}
                {searching && !boundClient ? <Spinner className="h-3 w-3" /> : null}
              </div>
              {boundClient ? (
                <div className="flex items-start gap-2 rounded-md border border-green-200 bg-white/70 px-2.5 py-1.5">
                  <Check size={15} className="mt-0.5 shrink-0 text-green-700" />
                  <div className="min-w-0 text-xs">
                    <div className="truncate text-sm font-medium">{boundClient.full_name}</div>
                    <div className="truncate text-slate-500">
                      {[boundClient.tel, boundClient.email].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                </div>
              ) : listOpen && results.length ? (
                <div ref={listRef} className="relative">
                <ul className="absolute right-0 top-0 z-30 max-h-72 w-full min-w-[20rem] divide-y divide-slate-100 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                  {results.map((client) => (
                    <li key={client.id}>
                      <button
                        type="button"
                        className="block w-full px-2.5 py-1.5 text-left hover:bg-action-50"
                        onClick={() => {
                          bindClient(client);
                          setListOpen(false);
                        }}
                      >
                        <div className="truncate text-sm font-medium">{client.full_name}</div>
                        <div className="truncate text-xs text-slate-500">
                          {[client.tel, client.email].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
                </div>
              ) : searchTerm.value.length >= 2 && !searching && !results.length ? (
                <p className="text-xs text-slate-600">{t("reservation.form.noClientFound")}</p>
              ) : null}
              {!boundClient ? (
                <p className="mt-1 text-xs text-slate-600">
                  {t("reservation.form.clientListHint")}
                </p>
              ) : null}
            </div>
          </div>
        </fieldset>

        <fieldset
          className="space-y-1.5 rounded-b border-t border-slate-200 bg-white p-2.5"
          disabled={receiptLocked}
        >
          {/* Dva riadky s popiskami nad poľami: kedy a v akom stave, potom
              čo a pre koho. Popisky vľavo by na to potrebovali šesť riadkov. */}
          <div className="grid grid-cols-2 items-start gap-x-2 gap-y-1.5 sm:grid-cols-[9rem_5.5rem_5.5rem_auto_auto] sm:justify-start sm:gap-x-4">
            <Cell
              label={t("reservation.date")}
              error={errors.date && t("errors.invalidDate")}
              className="col-span-2 sm:col-span-1"
            >
              <DateInput
                value={watch("date")}
                onChange={(value) => setValue("date", value, { shouldDirty: true, shouldValidate: true })}
              />
            </Cell>
            <Cell label={t("reservation.start")} error={errors.start && t("errors.required")}>
              <TimeInput {...gridTime}
                  value={watch("start")}
                  onChange={(value) => setValue("start", value, { shouldDirty: true, shouldValidate: true })}
                />
            </Cell>
            <Cell label={t("reservation.end")} error={errors.end && t("errors.required")}>
              <TimeInput {...gridTime}
                  value={watch("end")}
                  onChange={(value) => setValue("end", value, { shouldDirty: true, shouldValidate: true })}
                />
            </Cell>
            <Cell label={t("reservation.form.course")} className="col-span-2 sm:col-span-1">
              {/* Tlačidlá ako pri stave - kurzov je pár (SKI, SNB) a jedným
                  kliknutím sa prepnú rýchlejšie než cez rozbaľovací zoznam. */}
              <div className="flex flex-wrap gap-1">
                {codebooks.courses.map((c) => {
                  const active = watch("course_id") === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      title={c.name ?? undefined}
                      onClick={() => setValue("course_id", c.id, { shouldDirty: true })}
                      className={cn(
                        "rounded px-2.5 py-1 text-[13px] font-medium transition-all",
                        active
                          ? "bg-action-600 text-white"
                          : "bg-slate-100 text-slate-600 opacity-60 hover:bg-slate-200 hover:opacity-100",
                      )}
                    >
                      {c.key}
                    </button>
                  );
                })}
              </div>
            </Cell>
            <Cell label={t("reservation.form.status")} className="col-span-2 sm:col-span-1">
              <div className="flex flex-wrap gap-1">
                {codebooks.statuses.map((s) => {
                  const active = watch("status_id") === s.id;
                  const style =
                    STATUS_STYLES[s.id] ?? STATUS_STYLES[ReserveStatus.Preliminary];
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setValue("status_id", s.id, { shouldDirty: true })}
                      className={cn(
                        "rounded px-2.5 py-1 text-[13px] font-medium transition-all",
                        active ? style.active : `${style.inactive} opacity-50 hover:opacity-100`,
                      )}
                    >
                      {s.key}
                    </button>
                  );
                })}
              </div>
            </Cell>
          </div>

          {/* Ďalšie termíny — len pri zakladaní. Pri úprave by to znamenalo
              „z tejto lekcie sprav tri“, čo je iná operácia než „uprav ju“;
              rozpísaný kurz sa dopĺňa novým zakladaním. */}
          {!savedId ? (
            <>
              {extra.map((term, index) => (
                <Cell key={index} label={index === 0 ? t("reservation.form.moreTerms") : ""}>
                  <div className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_auto] gap-2 sm:grid-cols-[9rem_5.5rem_5.5rem_auto]">
                    <DateInput value={term.date} onChange={(date) => setTerm(index, { date })} />
                    <TimeInput {...gridTime} value={term.start} onChange={(start) => setTerm(index, { start })} />
                    <TimeInput {...gridTime} value={term.end} onChange={(end) => setTerm(index, { end })} />
                    <button
                      type="button"
                      className="btn-secondary h-[30px] w-fit px-2"
                      title={t("global.delete")}
                      onClick={() => setExtra(extra.filter((_, i) => i !== index))}
                    >
                      <X size={15} />
                    </button>
                  </div>
                </Cell>
              ))}
              <div>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[13px] font-medium
                             text-brand-600 underline-offset-2 hover:underline"
                  onClick={addTerm}
                >
                  <Plus size={14} />
                  {t("reservation.addTerm")}
                </button>
                {extra.length ? (
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t("reservation.moreTermsHint", { count: extra.length + 1 })}
                  </p>
                ) : null}
              </div>
            </>
          ) : null}

          <div className="grid grid-cols-2 items-start gap-x-2 gap-y-1.5 sm:grid-cols-[5rem_4.5rem_auto_9rem_minmax(0,1fr)]">
            {/* Len jazyky, ktoré škola učí. Vypnutý jazyk zostáva
                v číselníku kvôli starším lekciám, ale ponúkať sa nemá. */}
            <Cell label={t("reservation.form.language")}>
              <Select {...register("lang_id", { valueAsNumber: true })}>
                {codebooks.languages
                  .filter((l) => l.use !== false)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.key}
                    </option>
                  ))}
              </Select>
            </Cell>
            <Cell label={t("reservation.form.count")}>
              <Select {...register("numbers", { valueAsNumber: true })}>
                {Array.from(
                  {
                    length: Math.max(
                      grid.max_clients_per_reservation,
                      reservation?.numbers ?? 1,
                    ),
                  },
                  (_, i) => i + 1,
                ).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </Cell>
            <div className="flex flex-col justify-end gap-0.5 self-end pb-0.5">
              {multiCenter ? (
                blockId ? (
                  <span className="text-xs font-medium text-slate-600" title={t("centers.center")}>
                    {centerName(centerId)}
                  </span>
                ) : (
                  <CenterSelect value={ownCenterId} onChange={setOwnCenterId} />
                )
              ) : null}
              <Checkbox label={t("reservation.form.child")} {...register("is_children")} />
              <Checkbox
                label={t("reservation.form.schoolCourse")}
                {...register("is_school_course")}
              />
            </div>
            {/* Úroveň namiesto zaškrtávacieho políčka „začiatočník“ - je
                to tá istá informácia, len úplnejšia, a príznak si z nej
                server odvodí sám. */}
            <Cell label={t("clients.level")}>
              <Select
                {...register("levelid", {
                  setValueAs: (value) => (value === "" ? null : Number(value)),
                })}
              >
                {(codebooks?.levels ?? []).map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name || level.key}
                  </option>
                ))}
              </Select>
            </Cell>
            <Cell label={t("reservation.form.reservationNote")} className="col-span-2 sm:col-span-1">
              <Input maxLength={100} {...register("note")} />
            </Cell>
          </div>

          {/* Len označenie úhrady - platby škola eviduje inde. Platí pre celú
              objednávku s týmto variabilným symbolom. */}
          {simplePayments ? (
            <Cell label={t("reservation.form.paid")} className="sm:w-64">
              <Select {...register("client_paid", { valueAsNumber: true })}>
                {simplePaymentOptions(reservation?.client_paid).map((p) => (
                  <option key={p} value={p}>
                    {t(`reservation.payment.${p}`)}
                  </option>
                ))}
              </Select>
              {reservation?.payment_ref ? (
                <p className="mt-0.5 text-xs text-slate-500">{t("reservation.paidWholeOrder")}</p>
              ) : null}
            </Cell>
          ) : null}


          <p className="text-xs text-slate-500">{t("reservation.perLessonHint")}</p>
        </fieldset>
      </form>

      {/* Pred uložením orientačná cena podľa cenníka - nič sa nezapisuje,
          variabilný symbol sa pridelí až uložením. */}
      {!savedId && quote ? (
        <fieldset className="mt-2 rounded border border-dashed border-slate-300 bg-slate-50 p-2.5">
          <legend className="px-1 text-xs font-medium uppercase text-slate-500">
            {t("reservation.priceSection")}
          </legend>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-700">
                {formatMoney(Number.parseFloat(quote.total))}
                <span className="ml-1.5 rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-normal uppercase text-slate-600">
                  {t("reservation.quoteEstimate")}
                </span>
              </span>
              {quote.unpriced ? (
                <span className="text-xs text-amber-700">{t("payments.unpriced")}</span>
              ) : null}
              <span className="text-xs text-slate-500">
                {t("reservation.paymentRef")}:{" "}
                <span className="italic">{t("reservation.paymentRefAfterSave")}</span>
              </span>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPaymentsOpen(true)}
            >
              {t("global.details")}
            </button>
          </div>
          {accountPicker}
        </fieldset>
      ) : null}

      {/* Cena kurzu po uložení - skutočné položky a variabilný symbol.
          Editácia poplatkov je v okne „detaily platby“. */}
      {savedId ? (
        <fieldset className="mt-2 rounded border border-slate-200 bg-slate-50 p-2.5">
          <legend className="px-1 text-xs font-medium uppercase text-slate-500">
            {t("reservation.priceSection")}
          </legend>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-700">
                {formatMoney(feeTotal)}
              </span>
              {order && !simplePayments ? (
                <span className="text-xs text-slate-500">
                  {t("payments.received")}:{" "}
                  <span className="font-medium text-green-700">{formatMoney(order.paid)}</span>
                  {Number.parseFloat(order.due) > 0 ? (
                    <>
                      {" · "}
                      {t("payments.due")}:{" "}
                      <span className="font-medium text-brand-600">{formatMoney(order.due)}</span>
                    </>
                  ) : null}
                </span>
              ) : null}
              {savedRef ? (
                <span className="text-xs text-slate-500">
                  {t("reservation.paymentRef")}:{" "}
                  <span className="font-mono font-medium text-slate-700">{savedRef}</span>
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPaymentsOpen(true)}
            >
              {t("global.details")}
            </button>
          </div>
          {accountPicker}
        </fieldset>
      ) : null}

      {/* Odmena pre inštruktora - suma sa neukazuje, kým si ju niekto
          nevyžiada, a viewer si ju nevyžiada nikdy - výška odmeny nie je
          jeho vec. */}
      {!savedId && user?.can_write && rewardQuote ? (
        <fieldset className="mt-2 rounded border border-dashed border-slate-300 p-2">
          <legend className="px-1 text-xs font-medium uppercase text-slate-500">
            {t("reservation.rewardsSection")}
          </legend>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRewardsOpen(true)}
            >
              {t("reservation.showRewardDetails")}
            </button>
            <span className="text-xs text-slate-500">{t("reservation.quoteEstimate")}</span>
          </div>
        </fieldset>
      ) : null}

      {savedId && user?.can_write ? (
        <fieldset className="mt-2 rounded border border-slate-200 p-2">
          <legend className="px-1 text-xs font-medium uppercase text-slate-500">
            {t("reservation.rewardsSection")}
          </legend>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setRewardsOpen(true)}
          >
            {t("reservation.showRewardDetails")}
          </button>
        </fieldset>
      ) : null}

      {!user?.can_write ? (
        <div className="mt-2">
          <Alert variant="info">{t("errors.readOnly")}</Alert>
        </div>
      ) : null}

      {paymentsOpen && savedId ? (
        <PaymentDetailsModal
          reservation={{
            ...(reservation ?? ({} as ClientReservation)),
            id: savedId,
            payment_ref: savedRef,
            start: toISODateTime(watch("date"), watch("start")),
            end: toISODateTime(watch("date"), watch("end")),
          }}
          clientName={boundClient?.full_name || watch("sname") || ""}
          readOnly={!user?.can_write}
          onClose={() => setPaymentsOpen(false)}
        />
      ) : null}

      {paymentsOpen && !savedId && quote ? (
        <PaymentQuoteModal
          quote={quote}
          clientName={boundClient?.full_name || [watch("fname"), watch("sname")].filter(Boolean).join(" ")}
          onClose={() => setPaymentsOpen(false)}
        />
      ) : null}

      {rewardsOpen && !savedId && rewardQuote ? (
        <RewardQuoteModal
          quote={rewardQuote}
          instructorReservationId={blockId}
          bonusIds={bonusIds}
          onToggleBonus={(id) =>
            setBonusIds(bonusIds.includes(id) ? bonusIds.filter((x) => x !== id) : [...bonusIds, id])
          }
          onClose={() => setRewardsOpen(false)}
        />
      ) : null}

      {rewardsOpen && savedId ? (
        <LessonRewardModal
          clientReservationId={savedId}
          instructorReservationId={
            reservation?.instructor_reservation_id ?? blockId
          }
          instructorName={instructorName}
          readOnly={!user?.can_write}
          bonusIds={bonusIds}
          onBonusIdsChange={setBonusIds}
          onClose={() => setRewardsOpen(false)}
        />
      ) : null}
    </Modal>
  );
}

/** Riadok formulára: popisok vľavo, polia vpravo. `narrow` pre pravý stĺpec,
 *  kde sú popisky krátke („Jazyk:“, „Stav:“). */
/** Pole s popiskom nad sebou - pre riadky s viacerými poľami vedľa seba. */
function Cell({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string | false;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      {/* Popisky sú tie isté ako pri poliach vľavo („Stav:“) - nad poľom bez
          dvojbodky a s veľkým začiatočným písmenom. */}
      <span className="mb-0.5 block min-h-[1rem] text-[12px] font-semibold text-slate-600 first-letter:uppercase">
        {label.replace(/:\s*$/, "")}
      </span>
      {children}
      {error ? <p className="field-error">{error}</p> : null}
    </div>
  );
}

/** Spôsoby úhrady pri „len označení“. Aktuálny spôsob rezervácie sa doplní
 *  aj vtedy, keď sa ručne vyberať nedá (napr. „Z kreditu“ z čias plnej
 *  evidencie) - inak by výber ostal prázdny a uloženie by ho prepísalo. */
const SIMPLE_PAYMENT_TYPES: number[] = [
  PaymentType.Unpaid,
  PaymentType.Cash,
  PaymentType.Card,
  PaymentType.Transfer,
  PaymentType.Invoice,
  PaymentType.Other,
];

function simplePaymentOptions(current: number | null | undefined) {
  return current != null && !SIMPLE_PAYMENT_TYPES.includes(current)
    ? [...SIMPLE_PAYMENT_TYPES, current]
    : SIMPLE_PAYMENT_TYPES;
}

/** Polia, od ktorých závisí cena (dĺžka, počet osôb, jazyk) alebo predpis
 *  (zrušenie). Len pri ich zmene sa uhradená rezervácia nanečisto prepočíta
 *  (`paidChangeQuestion`); inak sa uloží bez otázky. */
const PRICE_FIELDS = ["date", "start", "end", "numbers", "lang_id", "status_id"] as const;

function Row({
  label,
  error,
  narrow,
  children,
}: {
  label: string;
  error?: string;
  narrow?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid items-center gap-x-2 gap-y-0.5",
        narrow
          ? "sm:grid-cols-[5.5rem_minmax(0,1fr)]"
          : "sm:grid-cols-[8.5rem_minmax(0,1fr)]",
      )}
    >
      <span className="text-[13px] font-semibold text-slate-700">{label}</span>
      <div className="min-w-0">
        {children}
        {error ? <p className="field-error">{error}</p> : null}
      </div>
    </div>
  );
}

/** Farba tlačidla stavu - PRE šedá, DEF zelená, ZRU červená. */
const STATUS_STYLES: Record<number, { active: string; inactive: string }> = {
  [ReserveStatus.Preliminary]: {
    active: "bg-slate-500 text-white",
    inactive: "bg-slate-100 text-slate-600 hover:bg-slate-200",
  },
  [ReserveStatus.Confirmed]: {
    active: "bg-green-600 text-white",
    inactive: "bg-green-100 text-green-800 hover:bg-green-200",
  },
  [ReserveStatus.Cancelled]: {
    active: "bg-red-600 text-white",
    inactive: "bg-red-100 text-red-800 hover:bg-red-200",
  },
};

/** Add hours to a `HH:MM` label, clamped to the school's closing time. */
function addHours(time: string, hours: number, endHour: number): string {
  const [h, m] = time.split(":").map((x) => Number.parseInt(x, 10));
  const total = Math.min(h + hours + m / 60, endHour);
  const hh = Math.floor(total);
  const mm = Math.round((total - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
